import { v4 as uuid } from 'uuid';

import * as actions from './actions';

import {
  DUAL,
  newTransactionSideDetails,
  setTransactionSideToDual,
} from 'bundles/backoffice/vouchers/helpers/transaction_side_details';
import { hasMatchingPayee } from 'bundles/backoffice/vouchers/helpers/commissions';
import { initializeCommission, recalculateBrokerCommissions } from './broker_commission_helpers';

export default function(state = null, action) {
  switch (action.type) {

  case actions.CHANGE_DEAL:
    return {
      ...state,
      ...action.changes,
      isDirty: true,
    };

  case actions.ADD_DEAL_USER:
    return {
      ...state,
      dealUsers: [...state.dealUsers, { localId: uuid() }],
      isDirty: true,
    };

  case actions.CHANGE_DEAL_USER: {
    return {
      ...state,
      dealUsers: state.dealUsers.map((dealUser) => {
        const key = action.dealUser.id ? 'id' : 'localId';
        if (dealUser[key] === action.dealUser[key]) {
          dealUser = { ...dealUser, ...action.changes };
        }
        return dealUser;
      }),
      isDirty: true,
    };
  }

  case actions.ADD_CONTACT_ROLE:
    return {
      ...state,
      contactRoles: [...state.contactRoles, { localId: uuid(), ...action.contactRole }],
      isDirty: true,
    };

  case actions.CHANGE_CONTACT_ROLE:
    return {
      ...state,
      isDirty: true,
      contactRoles: state.contactRoles.map((contactRole) => {
        if (isLocalMatch(action.contactRole, contactRole)) {
          contactRole = { ...contactRole, ...action.changes };
        }

        return contactRole;
      }),
    };

  case actions.ADD_COMMISSION: {
    const commission = initializeCommission({ ...action.attributes });

    if (action.attributes.transactionSide) {
      commission.transactionSideDetails = [newTransactionSideDetails(commission.transactionSide, true)];
    }

    return {
      ...state,
      commissions: [...state.commissions, commission],
      isDirty: true,
    };
  }

  case actions.CHANGE_COMMISSION:
    return {
      ...state,
      isDirty: true,
      commissions: recalculateBrokerCommissions(state, state.commissions.map((commission) => {
        if (isLocalMatch(action.commission, commission)) {
          commission = { ...commission, ...action.changes };
        }

        return commission;
      })),
    };

  case actions.CHANGE_COMMISSION_TRANSACTION_SIDE_DETAILS: {
    let targetCommission;
    const commissions = state.commissions.map((commission) => {
      return isLocalMatch(commission, action.commission)
        ? targetCommission = { ...commission }
        : commission;
    });

    targetCommission.transactionSideDetails = targetCommission.transactionSideDetails.map((details) => {
      return details.transactionSide === action.transactionSide
        ? { ...details, ...action.changes }
        : details;
    });

    return {
      ...state,
      isDirty: true,
      commissions: recalculateBrokerCommissions(state, commissions),
    };
  }

  case actions.CHANGE_COMMISSION_TRANSACTION_SIDE_PAYEE: {
    let targetCommission, existingCommissionForPayee;
    const commissions = state.commissions.map((commission) => {
      if (isLocalMatch(commission, action.commission)) {
        return targetCommission = { ...commission };
      } else if (hasMatchingPayee({ source: action.changes, target: commission })) {
        return existingCommissionForPayee = { ...commission };
      } else {
        return commission;
      }
    });

    if (existingCommissionForPayee) {
      // NOTE: This assumes that the UI prevents the user from selecting the same broker too many times
      const targetDetails = removeTransactionSideDetails(targetCommission, action.transactionSide);
      setTransactionSideToDual(existingCommissionForPayee);
      setTransactionSideDetails(existingCommissionForPayee, targetDetails);

      if (!targetCommission.transactionSideDetails.length) {
        // NOTE: This assumes the UI only allows brokers/contacts to be changed on unpersisted commissions
        commissions.splice(commissions.indexOf(targetCommission), 1);
      }
    } else if (targetCommission.transactionSide === DUAL) {
      const targetDetails = removeTransactionSideDetails(targetCommission, action.transactionSide);
      const newCommission = { ...action.changes, outside: targetCommission.outside, localId: uuid() };
      setTransactionSideDetails(newCommission, targetDetails);
      commissions.push(newCommission);
    } else {
      targetCommission.broker = action.changes.broker;
      targetCommission.contact = action.changes.contact;
    }

    return {
      ...state,
      isDirty: true,
      commissions: recalculateBrokerCommissions(state, commissions),
    };
  }

  case actions.SET_DEAL:
    return action.deal;

  default:
    return state;
  }
}

function isLocalMatch(actual, other) {
  const key = actual.id ? 'id' : 'localId';
  return actual[key] === other[key];
}

function removeTransactionSideDetails(commission, transactionSide) {
  let removedDetails;

  commission.transactionSideDetails = commission.transactionSideDetails.filter((details) => {
    if (details.transactionSide === transactionSide) {
      removedDetails = details;
      return false;
    } else {
      return true;
    }
  });

  commission.transactionSide = commission.transactionSideDetails[0]?.transactionSide;

  return removedDetails;
}

function setTransactionSideDetails(commission, details) {
  let set;
  commission.transactionSideDetails = (commission.transactionSideDetails || []).map((existingDetails) => {
    if (existingDetails.transactionSide === details.transactionSide) {
      set = true;
      return details;
    } else {
      return existingDetails;
    }
  });

  if (!set) {
    commission.transactionSideDetails.push(details);
  }

  if (commission.transactionSideDetails.length === 1) {
    commission.transactionSide = commission.transactionSideDetails[0].transactionSide;
  } else {
    commission.transactionSide = DUAL;
  }
}
